import { useDevices } from '../state/devices';
import { noteCallEvent } from './call-diagnostics';

type SinkContext = AudioContext & { setSinkId?: (id: string) => Promise<void> };
let context: SinkContext | null = null;

async function audio(): Promise<SinkContext> {
  context ??= new AudioContext() as SinkContext;
  if (context.state === 'suspended') await context.resume().catch(() => undefined);
  const { speakerId } = useDevices.getState();
  // Chrome/Edge can route to the chosen speaker; elsewhere the system default plays it.
  if (context.setSinkId && speakerId !== 'default') await context.setSinkId(speakerId).catch(() => undefined);
  return context;
}

/** Short synthesized tones: no audio files to ship or license. */
/**
 * Soft, low "wooden" cues (marimba-like): a sine with a faint octave, low-passed so nothing is
 * shrill, with a gentle attack and quick decay. Mid frequencies so laptop speakers still play them.
 */
async function tones(notes: [frequency: number, startS: number][], durationS = 0.24, volume = 0.38) {
  noteCallEvent('cue', notes.map(([hz]) => hz).join('>'));
  const ctx = await audio();
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 1300;
  filter.connect(ctx.destination);
  for (const [frequency, start] of notes) {
    const t = ctx.currentTime + start;
    for (const [ratio, level] of [[1, 1], [2, 0.18]] as const) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = frequency * ratio;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(volume * level, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + durationS);
      osc.connect(gain).connect(filter);
      osc.start(t);
      osc.stop(t + durationS + 0.02);
    }
  }
}

export const sounds = {
  /** Someone (or you) joined the room: rising. */
  join: () => void tones([[330, 0], [440, 0.11]]),
  /** Someone left: falling. */
  leave: () => void tones([[440, 0], [330, 0.11]]),
  /** Your own mic: short up / down blips. */
  micOn: () => void tones([[392, 0], [523, 0.07]], 0.16, 0.3),
  micOff: () => void tones([[523, 0], [392, 0.07]], 0.16, 0.3),
  /** Someone else turned their mic on ("about to talk"); quieter than your own. */
  othersMicOn: () => void tones([[392, 0], [523, 0.07]], 0.14, 0.16),
  /** Someone started / stopped sharing their screen. */
  shareStart: () => void tones([[330, 0], [392, 0.1], [523, 0.2]]),
  shareStop: () => void tones([[523, 0], [392, 0.1], [330, 0.2]]),
  /** The call is cut off and trying again: a soft up-and-back, repeated until it is back (never a steady tone). */
  reconnecting: () => void tones([[330, 0], [392, 0.13], [330, 0.26]], 0.2, 0.3),
  /** Back after being cut off: rising, a step brighter than "join". */
  reconnected: () => void tones([[392, 0], [523, 0.1], [659, 0.2]]),
  /** Speaker test in settings. */
  test: () => void tones([[330, 0], [440, 0.14], [523, 0.28]]),
};
